import { useEffect } from 'react';

export interface Shown {
  cid: string;
  mime: string;
  ts: number;
  who?: string;
  chat: string;
}

/**
 * One photo or video, full size, over everything else.
 *
 * Tapping a tile used to jump to the conversation it came from, which loses
 * the thing you were looking at to go and find it again. A picture opens as a
 * picture; the conversation is still one button away for when that is what you
 * wanted.
 *
 * The arrows walk the same list the grid is showing, so a viewer opened from
 * "Media" browses media and one opened from a filtered list stays inside it.
 */
export function Viewer({ items, at, onAt, onClose, onOpenChat }: {
  items: Shown[];
  at: number;
  onAt: (i: number) => void;
  onClose: () => void;
  onOpenChat: () => void;
}) {
  const it = items[at];

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && at > 0) onAt(at - 1);
      if (e.key === 'ArrowRight' && at < items.length - 1) onAt(at + 1);
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [at, items.length, onAt, onClose]);

  if (!it) return null;
  const when = new Date(it.ts).toLocaleString(undefined, {
    day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label="Photo">
      <header>
        <div className="who">
          <strong>{it.who ?? it.chat}</strong>
          <span className="fine">{it.chat} · {when}</span>
        </div>
        <span className="count">{at + 1} / {items.length}</span>
        <button className="ghost" onClick={onOpenChat}>Open in the conversation</button>
        <button className="x" onClick={onClose} aria-label="Close">×</button>
      </header>

      {/* O fundo fecha; o conteúdo não. Sem isto, clicar na própria foto para
          olhá-la de perto fechava a tela. */}
      <div className="stage" onClick={onClose}>
        <button className="step" disabled={at === 0} aria-label="Previous"
                onClick={(e) => { e.stopPropagation(); onAt(at - 1); }}>‹</button>

        <div className="frame" onClick={(e) => e.stopPropagation()}>
          {it.mime.startsWith('video/')
            ? <video key={it.cid} src={`/media/${it.cid}`} controls autoPlay playsInline />
            : <img key={it.cid} src={`/media/${it.cid}`} alt="" />}
        </div>

        <button className="step" disabled={at >= items.length - 1} aria-label="Next"
                onClick={(e) => { e.stopPropagation(); onAt(at + 1); }}>›</button>
      </div>
    </div>
  );
}
